import Input from "components/atoms/input/Input";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import PhoneInput from "components/atoms/input/phone/PhoneInput";
import TextArea from "components/atoms/input/TextArea";
import { ValidateWord } from "components/atoms/text/Text";
import { iAmAOptions } from "utils/options";
import * as s from "./TechStyled";
import { useAuth } from "components/Auth";
import AddressInput from "components/atoms/input/address/AddressInput";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { TechSupportSubmit } from "api/interfaces/helpInterface.interface";
import {
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { createTechSupport } from "api/helpAPI";
import { useMutation } from "react-query";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import MapUpload from "components/atoms/input/upload/map/MapUpload";
import SelectComponent from "components/atoms/select/SelectComponent";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RecordeVideoFileInput } from "./RecordedVideoFileInput";
import { checkEmpty, splitFullName, toTrim } from "utils/functions";
import useApiError from "hook/useApiError";
import { Text } from "components/atoms/text/Text";
import CenterContentPage from "components/templates/layout/CenterContentPage";
type Props = {};
enum TECH_USER_TYPE {
  consumerEU = "consumerEU",
  corporateEU = "corporateEU",
  distributor = "distributor",
  installer = "installer",
}

export enum UPDATE_TYPE {
  EMAIL,
  USER_TYPE,
  FIRST_NAME,
  LAST_NAME,
  PHONE,
  ADDRESS,
  DESC,
}

export interface InfoItem {
  label: string;
  input: React.ReactNode;
  essential?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export default function TechSupport(props: Props): JSX.Element {
  const { user } = useAuth();

  const imgRef = useRef<any>();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { lang, intlString, notifySuccess, notifyError } = useApiError();
  const navigate = useNavigate();
  const [imgFile, setImgFile] = useState<any>("");
  const [uploadFile, setUploadFile] = useState<File>();

  const [validate, setValidate] = useState<Validate>({
    description: false,
  });

  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
    description: "",
  });

  const [techSupport, setTechSupport] = useState<TechSupportSubmit>({
    email: user.email,
    userType: TECH_USER_TYPE.consumerEU,
    name: user.name,
    phone: "",
    address: "",
    description: "",
    firstName: splitFullName(user.name),
    lastName: splitFullName(user.name, true),
  });

  const [hasRecordedVideo, setHasRecordedVideo] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [fileUrl, setFileUrl] = useState<string>("");

  useEffect(() => {
    const blobUrl = searchParams.get("bloburl");
    if (!blobUrl) {
      return;
    }
    setHasRecordedVideo(true);
    fetch(blobUrl)
      .then((res: any) => {
        return res.blob();
      })
      .then((blob) => {
        const fileName = searchParams.get("filename") || "Recorded.webm";
        const file = new File([blob], fileName);
        setUploadFile(file);
        setFileUrl(blobUrl);
      })
      .catch((err) => {
        console.error(err);
        setHasRecordedVideo(false);
      });

    return () => {
      setHasRecordedVideo(false);
    };
  }, [searchParams]);

  const saveImgFile = (e: any) => {
    let files = e.target.files[0];
    setUploadFile(files);
    const reader = new FileReader();
    reader.readAsDataURL(files);
    return new Promise((resolve: any) => {
      reader.onload = () => {
        setImgFile(reader.result);
        resolve();
      };
    });
  };

  const onDeleteImgFile = () => {
    imgRef.current.value = "";
    setImgFile("");
  };

  const onChangeTech = useCallback(
    (type: UPDATE_TYPE, value: string | boolean) => {
      if (type === UPDATE_TYPE.EMAIL) {
        setTechSupport((info) => {
          return { ...info, email: value as string };
        });
      } else if (type === UPDATE_TYPE.USER_TYPE) {
        setTechSupport((info) => {
          return { ...info, userType: value as string };
        });
      } else if (type === UPDATE_TYPE.FIRST_NAME) {
        setTechSupport((info) => {
          return { ...info, firstName: value as string };
        });
        setTechSupport((info) => {
          return { ...info, name: (value + " " + info.lastName) as string };
        });
      } else if (type === UPDATE_TYPE.LAST_NAME) {
        setTechSupport((info) => {
          return { ...info, lastName: value as string };
        });
        setTechSupport((info) => {
          return { ...info, name: (info.firstName + " " + value) as string };
        });
      } else if (type === UPDATE_TYPE.PHONE) {
        setTechSupport((info) => {
          return { ...info, phone: value as string };
        });
      } else if (type === UPDATE_TYPE.ADDRESS) {
        setTechSupport((info) => {
          return { ...info, address: value as string };
        });
      } else if (type === UPDATE_TYPE.DESC) {
        setTechSupport((info) => {
          return { ...info, description: value as string };
        });
      }
    },
    []
  );

  const onChangeAddress = useCallback(
    (type: UPDATE_TYPE, value: string | boolean) => {
      if (type === UPDATE_TYPE.ADDRESS) {
        setTechSupport((info) => {
          return { ...info, address: value as string };
        });
      }
    },
    []
  );

  const imageFileInput = {
    label: "Upload File",
    input: (
      <MapUpload
        onUpload={saveImgFile}
        onDelete={onDeleteImgFile}
        ref={imgRef}
      />
    ),

    placeholder: "",
  };

  const videoFileInput = {
    label: "Recorded Video File",
    input: <RecordeVideoFileInput fileUrl={fileUrl} uploadFile={uploadFile} />,
    essential: false,
    disabled: true,
  };

  const contactInfo: InfoItem[] = [
    {
      label: "Email",
      input: (
        <Input
          placeholder={"Please enter an email"}
          value={techSupport.email}
          onChange={(e) =>
            onChangeTech(UPDATE_TYPE.EMAIL, toTrim(e.target.value))
          }
          disabled
          data-testid="help-tech-support-input"
        />
      ),

      placeholder: "Please enter an email",
      essential: true,
      disabled: true,
    },
    {
      label: "I Am A(n)",
      input: (
        <SelectComponent
          options={iAmAOptions}
          value={techSupport.userType}
          onChange={(value) =>
            onChangeTech(UPDATE_TYPE.USER_TYPE, value as string)
          }
          data-testid={`help-tech-support-selects-user-type`}
        />
      ),

      essential: true,
      disabled: false,
    },
    {
      label: "First Name",
      input: (
        <Input
          placeholder={"Please enter a first name"}
          value={
            techSupport.firstName !== undefined ? techSupport.firstName : ""
          }
          onChange={(e) =>
            onChangeTech(UPDATE_TYPE.FIRST_NAME, e.currentTarget.value)
          }
          data-testid="help-tech-support-input-name"
        />
      ),

      placeholder: "Please enter a first name",
      essential: false,
      disabled: false,
    },
    {
      label: "Last Name",
      input: (
        <Input
          placeholder={"Please enter a first name"}
          value={techSupport.lastName !== undefined ? techSupport.lastName : ""}
          onChange={(e) =>
            onChangeTech(UPDATE_TYPE.LAST_NAME, e.currentTarget.value)
          }
          data-testid="help-tech-support-input-last-name"
        />
      ),

      placeholder: "Please enter a last name",
      essential: false,
      disabled: false,
    },
    // {
    //   label: "Company Name",
    //   placeholder: "Enter company name",
    // },
    {
      label: "Phone Number",
      input: (
        <PhoneInput
          selected={techSupport.phone !== undefined ? techSupport.phone : ""}
          onChange={(phoneNumber) => {
            onChangeTech(UPDATE_TYPE.PHONE, phoneNumber);
          }}
          data-testid="help-tech-support-phone-input"
        />
      ),

      essential: false,
      disabled: false,
    },
    {
      label: "Address",
      input: (
        <AddressInput
          isLoaded={true}
          value={techSupport.address}
          onChange={(e) =>
            onChangeAddress(UPDATE_TYPE.ADDRESS, e.currentTarget.value)
          }
          onChangeLocationSelect={(
            address: google.maps.LatLngLiteral,
            addressString?: string
          ) => {
            if (addressString !== undefined) {
              onChangeTech(UPDATE_TYPE.ADDRESS, addressString);
            }
          }}
          data-testid="help-tech-support-address-input"
        />
      ),

      placeholder: "Please enter an address",
    },
    ...(hasRecordedVideo ? [videoFileInput] : [imageFileInput]),
  ];

  const productInfo: InfoItem[] = [
    {
      label: "Problem Description",
      input: (
        <Fragment>
          <TextArea
            placeholder="Tell us what's wrong"
            value={techSupport.description}
            onChange={(e) =>
              onChangeTech(UPDATE_TYPE.DESC, e.currentTarget.value)
            }
            data-testid="help-tech-support-textarea"
          />

          {validate.description && (
            <ValidateWord data-testid="help-tech-support-validate-word">
              {validateMsg.description}
            </ValidateWord>
          )}
        </Fragment>
      ),

      essential: true,
    },
  ];

  const mutationCreateTechSupport = useMutation(createTechSupport, {
    onSuccess: (res: any) => {
      notifySuccess(intlString(lang.label.about.notify.tech.send.success));
      if (searchParams.get("bloburl")) {
        navigate(-1);
        return;
      }
      setSearchParams({});
      setTechSupport((info) => {
        return { ...info, description: "" };
      });
    },
    onError: () => {
      notifyError(intlString(lang.label.about.notify.tech.send.fail));
    },
  });

  const onSubmit = useCallback(() => {
    setValidate((info) => {
      return { ...info, description: false };
    });
    setValidateMsg((info) => {
      return { ...info, description: "" };
    });

    if (checkEmpty(techSupport.description)) {
      setValidate((info) => {
        return { ...info, description: true };
      });
      setValidateMsg((info) => {
        return {
          ...info,
          description: intlString(lang.validateMsg.help.about.emptyIssue),
        };
      });
      return;
    }

    const formData = new FormData();
    if (imgFile !== undefined && imgFile !== "" && uploadFile !== undefined) {
      formData.append("file", uploadFile as File);
    }

    if (hasRecordedVideo) {
      formData.append("file", uploadFile as File);
    }

    const json = JSON.stringify(techSupport);
    const blob = new Blob([json], { type: "application/json" });
    formData.append("techSupportFormDto", blob);

    mutationCreateTechSupport.mutate({
      accountId: selectedAccount.accountId, // Account -> UpdateRequest
      form: formData,
    });
  }, [
    hasRecordedVideo,
    imgFile,
    intlString,
    lang.validateMsg.help.about.emptyIssue,
    mutationCreateTechSupport,
    selectedAccount.accountId,
    techSupport,
    uploadFile,
  ]);

  return (
    <CenterContentPage
      titleComponent="Tech Support Form"
      buttonLabel="Submit"
      onClickButton={onSubmit}
    >
      <Text bold style={{ width: "100%" }}>
        Your Contact Information
      </Text>
      <s.FormWrapper>
        {contactInfo.map((item, index) => (
          <ModalInput label={item.label} essential={item.essential} key={index}>
            {item.input ? (
              item.input
            ) : (
              <Input
                placeholder={item.placeholder}
                isValidated={!validate.description}
              />
            )}
          </ModalInput>
        ))}
      </s.FormWrapper>
      {productInfo.map((item, index) => (
        <ModalInput
          label={item.label}
          essential={item.essential ?? false}
          key={index}
        >
          {item.input}
        </ModalInput>
      ))}
    </CenterContentPage>
  );
}
